import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Buildings, Storefront, ArrowRight, Gift } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";

const HERO = "https://images.unsplash.com/photo-1706074740295-d7a79c079562?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2Nzh8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBvZmZpY2UlMjBnbGFzcyUyMHJlZmxlY3Rpb258ZW58MHx8fHwxNzg1ODYzNjM1fDA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [ref, setRef] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const r = params.get("ref");
    const wantsSignup = params.get("signup") === "1" || params.get("mode") === "register";
    if (r) {
      localStorage.setItem("citehub_ref", r);
      setRef(r);
      setMode("register");
    } else {
      const stored = localStorage.getItem("citehub_ref");
      if (stored) setRef(stored);
      if (wantsSignup) setMode("register");
    }
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
      } else {
        await register(name, email, password, ref);
      }
      toast.success(mode === "login" ? "Welcome back!" : "Account created!");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Left: form */}
      <div className="flex flex-col justify-center px-6 sm:px-12 lg:px-20 py-12 max-w-xl w-full mx-auto">
        <div className="mb-12">
          <img src="/citehub-logo.png" alt="Cite-Hub" className="h-10 w-auto" />
        </div>

        <p className="text-xs font-bold uppercase tracking-[0.14em] text-[#0055FF] mb-3">
          {mode === "login" ? "Welcome back" : "Get started free"}
        </p>
        <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter text-slate-900 leading-[1.05]">
          {mode === "login" ? "Sign in to your citations." : "Own every local listing."}
        </h1>
        <p className="mt-4 text-base text-slate-600 leading-relaxed">
          Manage your business info once, then track and publish it across 138 US directories.
        </p>

        <form onSubmit={submit} className="mt-9 space-y-5">
          {ref && mode === "register" && (
            <div data-testid="referral-note" className="flex items-center gap-2.5 rounded-md bg-blue-50 border border-blue-100 px-3.5 py-2.5 text-sm text-blue-800">
              <Gift weight="fill" size={18} className="text-[#0055FF] shrink-0" />
              <span>You were referred by <strong>{ref}</strong> — welcome! Have a promo code? Apply it at checkout.</span>
            </div>
          )}
          {mode === "register" && (
            <div className="space-y-2">
              <Label htmlFor="name" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Full name</Label>
              <Input id="name" data-testid="register-name-input" value={name} onChange={(e) => setName(e.target.value)}
                required placeholder="Jane Doe" className="h-11" />
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="email" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Email</Label>
            <Input id="email" type="email" data-testid="login-email-input" value={email} onChange={(e) => setEmail(e.target.value)}
              required placeholder="you@company.com" className="h-11" />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password" className="text-xs font-bold uppercase tracking-[0.1em] text-slate-500">Password</Label>
            <Input id="password" type="password" data-testid="login-password-input" value={password} onChange={(e) => setPassword(e.target.value)}
              required placeholder="••••••••" className="h-11" />
          </div>

          <Button type="submit" data-testid="login-submit-button" disabled={busy}
            className="w-full h-11 bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold group">
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            <ArrowRight size={18} className="ml-1.5 transition-transform group-hover:translate-x-0.5" />
          </Button>
        </form>

        <div className="flex items-center gap-4 my-6">
          <div className="h-px flex-1 bg-slate-200" />
          <span className="text-xs font-semibold uppercase tracking-wider text-slate-400">or</span>
          <div className="h-px flex-1 bg-slate-200" />
        </div>

        <Button type="button" variant="secondary" data-testid="google-login-button" onClick={googleLogin}
          className="w-full h-11 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold">
          <img alt="" src="https://www.google.com/favicon.ico" className="h-4 w-4 mr-2" />
          Continue with Google
        </Button>

        <p className="mt-8 text-sm text-slate-600">
          {mode === "login" ? "No account yet?" : "Already have an account?"}{" "}
          <button type="button" data-testid="toggle-auth-mode"
            onClick={() => setMode(mode === "login" ? "register" : "login")}
            className="font-semibold text-[#0055FF] hover:text-[#0044CC] underline underline-offset-2">
            {mode === "login" ? "Create one" : "Sign in"}
          </button>
        </p>
      </div>

      {/* Right: hero */}
      <div className="hidden lg:block relative overflow-hidden">
        <img src={HERO} alt="Modern office" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-slate-900/55" />
        <div className="relative h-full flex flex-col justify-end p-14 text-white">
          <div className="space-y-6 max-w-md">
            <Stat icon={Buildings} label="138 US directories" text="Google, Yelp, Bing, Apple Maps, BBB, Foursquare and more." />
            <Stat icon={Storefront} label="One source of truth" text="Consistent Name, Address & Phone everywhere you list." />
            <Stat icon={MapPin} label="Track every submission" text="See what's live, in progress or rejected at a glance." />
          </div>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ icon: Icon, label, text }) => (
  <div className="flex gap-4">
    <div className="h-11 w-11 shrink-0 rounded-md bg-white/15 backdrop-blur-sm flex items-center justify-center">
      <Icon weight="duotone" size={22} className="text-white" />
    </div>
    <div>
      <p className="font-display font-bold text-lg leading-tight">{label}</p>
      <p className="text-sm text-white/70 mt-0.5">{text}</p>
    </div>
  </div>
);
